import { Link } from 'react-router-dom';
import PageHeader from '@/components/feature/PageHeader';
import {
  aboutPrinciples,
  contactEmail,
  siteStats,
  siteTimeline,
  testimonials,
} from '@/mocks/site';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      name: '关于档案中心｜独家黑料档案中心',
      inLanguage: 'zh-CN',
      description: '介绍独家黑料档案中心的定位、整理理念、内容原则与联系方式。',
    },
    {
      '@type': 'ContactPage',
      name: '联系独家黑料档案中心',
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'Organization',
      name: '独家黑料档案中心',
      email: contactEmail,
      url: 'https://hnitsc.cn/about',
      description: '以公开可核对材料整理人物档案与历史记录的独立档案中心。',
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <PageHeader
        crumbs={[
          { label: '首页', to: '/' },
          { label: '关于档案中心' },
        ]}
        eyebrow="关于我们"
        title="我们只做一件事：把材料放回它原本的时间位置"
        description="独家黑料档案中心是一个以公开材料为基础的资料整理站点。我们不生产结论，只把散落的通告、节目单、场记单与公开访谈按日期摆回原位，让脉络自己浮现。"
        image="https://readdy.ai/api/search-image?query=Quiet%20archival%20reading%20room%20with%20tall%20wooden%20shelves%20and%20a%20single%20reading%20lamp%2C%20warm%20amber%20light%2C%20aged%20cream%20tones%2C%20calm%20editorial%20atmosphere%2C%20no%20people&width=1400&height=600&seq=about-banner-06&orientation=landscape"
        imageAlt="安静的档案阅览室与书架，象征档案中心的整理氛围"
        imageTitle="关于档案中心 · 独家黑料档案中心"
      />

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                从一份场记单开始的整理工作
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  事情的起点很朴素：一份旧场记单上有两处涂改，而这两处涂改恰好解释了当年一次临阵换角的时间差。
                  那是我们第一次意识到，很多被反复转述的旧事，其实只是缺少一份能对齐时间的材料。
                </p>
                <p>
                  于是「独家黑料档案中心」有了自己的定位——不做爆料，不做猜测，只做整理。
                  我们收集公开通讯稿、节目单、场记单、版权登记与公开访谈，把它们按日期、按人物、按事件重新归档，
                  并明确标注每一处疑点。
                </p>
                <p>
                  我们相信，读者不需要被别人替自己下判断。把材料摆整齐，把缺口标出来，
                  剩下的判断交回给愿意读完的人，这才是档案该有的样子。
                </p>
              </div>

              <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-background-200 pt-8 sm:grid-cols-4">
                {siteStats.map((stat) => (
                  <div key={stat.label}>
                    <dd className="font-heading text-2xl font-black text-foreground-950">
                      {stat.value}
                      <span className="ml-1 text-sm font-medium text-primary-700">{stat.unit}</span>
                    </dd>
                    <dt className="mt-1 text-xs text-foreground-500">{stat.label}</dt>
                  </div>
                ))}
              </dl>
            </div>

            <div className="flex flex-col gap-5">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20aged%20paper%20documents%20with%20pencil%20annotations%20and%20a%20magnifying%20glass%20on%20a%20wooden%20desk%2C%20warm%20amber%20light%2C%20cream%20tones%2C%20editorial%20archival%20mood%2C%20no%20people&width=800&height=600&seq=about-desk-07&orientation=landscape"
                  alt="带有铅笔批注的旧文件与放大镜，象征档案条目的考据过程"
                  title="档案考据 · 独家黑料档案中心"
                  className="h-56 w-full object-top object-cover md:h-64"
                />
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">网站定位</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  一个面向研究与阅读的公开资料索引站点，覆盖影视、音乐、电视与传媒四个领域，
                  提供人物档案、历史记录与深度专题三类内容。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            四条写进流程里的整理原则
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这些原则不是口号，它们决定了每一条档案能不能被收录、以及应该以什么形式出现。
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {aboutPrinciples.map((item) => (
              <div
                key={item.title}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-primary-700">
                <span className="inline-block h-px w-6 bg-primary-500"></span>
                发展理念
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一条脉络，比十个结论都更有用
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们更愿意把时间花在把材料对齐上，而不是急着给出一个听起来痛快的说法。
                下面是档案中心整理方法自身的几个关键节点。
              </p>
            </div>

            <ol className="space-y-4">
              {siteTimeline.map((event) => (
                <li
                  key={event.year}
                  className="flex gap-5 rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="font-heading text-xl font-black text-primary-700">{event.year}</span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {event.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{event.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-secondary-200 bg-secondary-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            读者怎么说
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            {testimonials.map((t) => (
              <blockquote
                key={t.name}
                className="flex flex-col rounded-lg border border-secondary-200 bg-background-50 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-secondary-500" aria-hidden="true"></i>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">{t.quote}</p>
                <footer className="mt-4 border-t border-secondary-200 pt-4">
                  <p className="font-heading text-sm font-semibold text-foreground-950">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto flex max-w-content flex-col items-start justify-between gap-6 px-4 py-12 md:flex-row md:items-center md:px-8 md:py-16">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              联系我们
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              发现条目有误、希望提供可公开核对的材料，或需要转载授权，都可以直接来信。
              我们会在核对之后回复，并在必要时更新对应条目。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-700 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              <i className="ri-mail-line" aria-hidden="true"></i>
              {contactEmail}
            </a>
            <Link
              to="/topics"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-300 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-800"
            >
              浏览秘史专题
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
    </>
  );
}